/* eslint-disable jsx-a11y/anchor-is-valid */
import {process, State} from "@progress/kendo-data-query"
import {Button, ToolbarSpacer} from "@progress/kendo-react-buttons"
import {ExcelExport, ExcelExportColumn} from "@progress/kendo-react-excel-export"
import {
    Grid,
    GridCellProps,
    GridColumn,
    GridColumnMenuCheckboxFilter,
    GridFooterCellProps,
    GridNoRecords,
    GridToolbar,
} from "@progress/kendo-react-grid"
import {useInternationalization} from "@progress/kendo-react-intl"
import {Tooltip} from "@progress/kendo-react-tooltip"
import dayjs from "dayjs"
import {useEffect, useRef, useState} from "react"
import {
    Booking,
    fetchBookingsDataAction,
    NewBookingParams,
    printforDVSA,
    setBookingFiltersAction,
    setNewBookingDetail,
    setSelectedBookingId,
} from "../../containers/MotBooking/bookingSlice"
import {RootState} from "../../rootReducer"
import {columnProps} from "../../shared/ActiveGridFilter"
import {SpinnerGrid} from "../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../store"
import {processExportData, calltoPreparerDealerName} from "../../utils/common"
import {MDBIcon} from "mdbreact"

const MotGrid = ({toggleEditBookingModal}: {toggleEditBookingModal: (bookingType: number, state: boolean) => void}) => {
    const dispatch = useAppDispatch()
    const intl = useInternationalization()

    const selectedDealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer)
    const bookings = useAppSelector((state: RootState) => state.containers.motBooking.bookings)
    const loading = useAppSelector((state: RootState) => state.containers.motBooking.gettingBookings)
    const bookingFilters = useAppSelector((state: RootState) => state.containers.motBooking.bookingFilters)
    const scheduleForDay = useAppSelector((state: RootState) => state.containers.motBooking.hasScheduleForDay)
    const newbkstartDate = useAppSelector((state: RootState) => state.containers.motBooking.NewBKStartDate)
    const userDealers = useAppSelector((state: RootState) => state.application.userDealers)

    const initialState: State = {skip: 0, take: 100}
    const [dataState, setDataState] = useState(initialState)

    useEffect(() => {
        const filterValues = {
            start: bookingFilters.start,
            selectedDealer: selectedDealer,
        }
        dispatch(setBookingFiltersAction(filterValues))
        dispatch(fetchBookingsDataAction(filterValues))
    }, [])

    useEffect(() => {
        setDataState(initialState)
    }, [bookings])

    const overLappingColor = (val: number) => (val > 0 ? {backgroundColor: "#e9e6c3d1"} : {backgroundColor: ""})

    const isBookingCancelledOrNoShow = (props: GridCellProps) =>
        props.dataItem["bookingTypeID"] === 1 &&
        (props.dataItem["bookingStatus"] === "Cancelled" || props.dataItem["bookingStatus"] === "No Show")

    const CustomColumnCell = (props: GridCellProps) => {
        var classCancelled = isBookingCancelledOrNoShow(props) ? "grid-cancelled-bk" : ""

        return (
            <td
                style={props.style}
                className={`${props.className} ${classCancelled}`}
                colSpan={props.colSpan}
                role={"gridcell"}
                title={props.dataItem[`${props.field}`]}
            >
                {props.dataItem[`${props.field}`]}
            </td>
        )
    }
    const CustomerColumnCell = (props: GridCellProps) => {
        var classCancelled = isBookingCancelledOrNoShow(props)
            ? "grid-cancelled-bk k-grid-content-sticky"
            : "k-grid-content-sticky"

        return (
            <td
                style={{...overLappingColor(props.dataItem.overLapping), ...props.style}}
                className={`${props.className} ${classCancelled}`}
                colSpan={props.colSpan}
                role={"gridcell"}
                title={props.dataItem[`${props.field}`]}
            >
                {props.dataItem[`${props.field}`]}
            </td>
        )
    }

    const FeeColumnCell = (props: GridCellProps) => {
        var classCancelled = isBookingCancelledOrNoShow(props) ? "grid-cancelled-bk " : ""

        return (
            <td className={`text-end pe-4 ${classCancelled}`}>
                {props.dataItem.bookingTypeID === 1
                    ? intl.formatNumber(props.dataItem[`${props.field}`], {style: "currency", minimumFractionDigits: 2})
                    : ""}
            </td>
        )
    }
    const DurationCell = (props: GridCellProps) => {
        var classCancelled = isBookingCancelledOrNoShow(props)
            ? "grid-cancelled-bk k-grid-content-sticky"
            : "k-grid-content-sticky"

        return (
            <td
                className={`${props.className} k-column-align-center ${classCancelled}`}
                style={{...overLappingColor(props.dataItem.overLapping), ...props.style}}
                colSpan={props.colSpan}
                role={"gridcell"}
            >
                {props.dataItem[`${props.field}`]}
            </td>
        )
    }

    const VehicleRegColumnCell = (props: GridCellProps) => {
        const regNumber = props.dataItem["registration"] ?? ""
        var classCancelled = isBookingCancelledOrNoShow(props)
            ? "grid-cancelled-bk k-grid-content-sticky"
            : "k-grid-content-sticky"

        return (
            <td
                style={{...overLappingColor(props.dataItem.overLapping), ...props.style}} // this applies styles that lock the column at a specific position
                className={`${props.className} ${classCancelled}`} // this adds classes needed for locked columns
                colSpan={props.colSpan}
                role={"gridcell"}
                title={regNumber}
            >
                {regNumber}
            </td>
        )
    }

    const TestColumnCell = (props: GridCellProps) => {
        const testStationName = props.dataItem["testStationName"] ?? ""
        var classCancelled = isBookingCancelledOrNoShow(props) ? "grid-cancelled-bk " : ""

        return (
            <td title={testStationName} key={crypto.randomUUID()} className={`${classCancelled}`}>
                {testStationName}
            </td>
        )
    }

    const openEditBookingDialog = (e: any, props: any) => {
        e.preventDefault()
        dispatch(setSelectedBookingId(props.dataItem.bookingID))
        toggleEditBookingModal(props.dataItem.bookingTypeID, true)
    }

    const _export = useRef<ExcelExport | null>(null)
    const excelExport = () => {
        if (_export.current !== null) {
            _export.current.save()
        }
    }

    const printDvsa = () => {
        const filterValues = {
            start: bookingFilters.start,
            selectedDealer: selectedDealer,
        }
        dispatch(printforDVSA(filterValues))
    }

    const excelExportFn = () => {
        return (
            <ExcelExport
                fileName={crypto.randomUUID()}
                data={exportExcelData(processExportData(bookings, dataState))}
                ref={_export}
            >
                <ExcelExportColumn field="bookingRef" title="Booking Ref" />
                <ExcelExportColumn field="testStartTime" title="Start" />
                <ExcelExportColumn field="testEnd" title="End" />
                <ExcelExportColumn field="duration" title="Duration" />
                <ExcelExportColumn field="registration" title="Vehicle" />
                <ExcelExportColumn field="companyName" title="Customer Name" />
                <ExcelExportColumn field="preparerID" title="PreparerID" />
                <ExcelExportColumn field="testStationName" title="TestStation" />
                <ExcelExportColumn field="jobNumber" title="Job No." />
                <ExcelExportColumn field="customerOrderRef" title="Customer Ref" />
                <ExcelExportColumn field="bookingStatus" title="Status" />
                <ExcelExportColumn field="reminderCall" title="Reminder call" />
                <ExcelExportColumn field="testFee" title="Test Fee" cellOptions={{format: "[$£-en-GB]#,##0.00"}} />
                <ExcelExportColumn field="laneFee" title="Lane Fee" cellOptions={{format: "[$£-en-GB]#,##0.00"}} />
                <ExcelExportColumn field="total" title="Total" cellOptions={{format: "[$£-en-GB]#,##0"}} />
            </ExcelExport>
        )
    }
    const setBookingRegLinkColor = (dataItem: Booking) => {
        if (dataItem.bookingTypeID === 1 && dataItem.internal) {
            return "#447F83"
        } else if (dataItem.bookingTypeID === 2) {
            return "#B83954"
        }
        return "#D65549"
    }
    const ReminderColumnCell = (props: GridCellProps) => {
        var classCancelled = isBookingCancelledOrNoShow(props) ? "grid-cancelled-bk " : ""

        return (
            <td>
                <span
                    className={`${
                        props.dataItem[`${props.field}`] === "Pending" ? "text-danger" : ""
                    } ${classCancelled}`}
                >
                    {props.dataItem[`${props.field}`]}
                </span>
            </td>
        )
    }

    const AddLinkCell = (props: any) => {
        return (
            <td
                className={`${
                    isBookingCancelledOrNoShow(props)
                        ? "grid-cancelled-bk k-grid-content-sticky"
                        : "k-grid-content-sticky"
                }`}
                style={{
                    left: "0px",
                    ...overLappingColor(props.dataItem.overLapping),
                    color: setBookingRegLinkColor(props.dataItem),
                }}
            >
                <a
                    style={{
                        textDecoration: "underline",
                        cursor: "pointer",
                    }}
                    onClick={event => openEditBookingDialog(event, props)}
                >
                    {props.dataItem.bookingRef}
                </a>
            </td>
        )
    }

    const TimeFormatCell = (props: GridCellProps) => {
        const dateVal =
            props.dataItem[`${props.field}`] == null || props.dataItem[`${props.field}`] === ""
                ? ""
                : intl.formatDate(new Date(props.dataItem[`${props.field}`]), {time: "short"})
        var classCancelled = isBookingCancelledOrNoShow(props)
            ? "grid-cancelled-bk k-grid-content-sticky"
            : "k-grid-content-sticky"
        return (
            <td
                style={{...overLappingColor(props.dataItem.overLapping), ...props.style}} // this applies styles that lock the column at a specific position
                className={`${props.className} ${classCancelled}`} // this adds classes needed for locked columns
                colSpan={props.colSpan}
                role={"gridcell"}
            >
                {dateVal}
            </td>
        )
    }

    const totalFeeFooterCell = (props: GridFooterCellProps) => {
        const field = props.field || ""
        const total = process(bookings, dataState).data.reduce(
            (acc: any, current: {[x: string]: any}) => acc + current[field],
            0,
        )
        return (
            <td className="text-end" colSpan={props.colSpan} style={props.style}>
                {intl.formatNumber(total, {style: "currency", maximumFractionDigits: 2})}
            </td>
        )
    }

    const commonFooterCell = (props: GridFooterCellProps) => <td style={props.style}></td>

    const exportExcelData = (bookings: Booking[]) =>
        bookings!.map(({testStartTime, testStart, testEnd, ...x}) => ({
            ...x,
            testStartTime: intl.formatDate(dayjs(testStartTime).toDate(), "HH:mm"),
            testEnd: intl.formatDate(dayjs(testEnd).toDate(), "HH:mm"),
        }))

    return (
        <div className="position-relative" id="grid-booking">
            {loading && <SpinnerGrid />}
            {bookings != null && (
                <Tooltip openDelay={100} position="bottom" anchorElement="pointer">
                    {excelExportFn()}
                    <Grid
                        style={{
                            height: "calc(100vh - 13.8rem)",
                        }}
                        data={process(bookings, dataState)}
                        {...dataState}
                        pageable={true}
                        sortable={true}
                        onDataStateChange={e => setDataState(e.dataState)}
                    >
                        <GridNoRecords>&nbsp;</GridNoRecords>
                        <GridToolbar>
                            <Button
                                fillMode="flat"
                                themeColor={"primary"}
                                className="buttons-container-button"
                                iconClass="fa fa-plus"
                                onClick={() => {
                                    const selectedPreparerDealerName = calltoPreparerDealerName(
                                        userDealers,
                                        selectedDealer?.dealerId,
                                    )
                                    let params = {
                                        dealerId: selectedDealer?.dealerId,
                                        // testStart: newbkstartDate,
                                        // testEnd: newbkstartDate,
                                        testStart: dayjs(newbkstartDate).startOf("day").toDate().toString(),
                                        testEnd: dayjs(newbkstartDate).startOf("day").toDate().toString(),
                                        testPackageID: null,
                                        registration: null,
                                        customer: null,
                                        preparerDealerName: "",
                                        preparedByDealer: false,
                                        // preparerDealerName:
                                        //     selectedPreparerDealerName !== undefined
                                        //         ? selectedPreparerDealerName
                                        //         : null,
                                        // preparedByDealer: selectedPreparerDealerName !== undefined ? true : false,
                                    } as NewBookingParams
                                    dispatch(setSelectedBookingId(0))
                                    dispatch(setNewBookingDetail(params))
                                    toggleEditBookingModal(1, true)
                                }}
                                disabled={!scheduleForDay}
                            >
                                New Booking
                            </Button>
                            <Button
                                fillMode="flat"
                                themeColor={"primary"}
                                className="buttons-container-button"
                                iconClass="fa fa-file-excel"
                                onClick={excelExport}
                                disabled={process(bookings, dataState).total === 0}
                            >
                                Export to Excel
                            </Button>
                            <Button
                                fillMode="flat"
                                themeColor={"primary"}
                                className="buttons-container-button"
                                iconClass="fas fa-file-pdf"
                                onClick={printDvsa}
                                disabled={
                                    process(bookings, dataState).total === 0 ||
                                    !bookings.some(x => x.bookingTypeID === 1)
                                }
                            >
                                Print for DVSA
                            </Button>
                            <ToolbarSpacer />
                            {bookings.some(a => a.overLapping > 0) ? (
                                <span style={{marginRight: 20}}>
                                    <MDBIcon
                                        className="me-1 mt-1"
                                        fas
                                        icon="exclamation-triangle"
                                        size="default"
                                        color="danger"
                                    />
                                    Overlapped bookings exist
                                </span>
                            ) : (
                                ""
                            )}
                        </GridToolbar>
                        <GridColumn
                            {...columnProps("bookingRef", dataState)}
                            field="bookingRef"
                            title="Booking Ref"
                            cell={props => <AddLinkCell {...props} />}
                            locked
                            width={150}
                            footerCell={commonFooterCell}
                        />
                        <GridColumn
                            sortable={true}
                            cell={TimeFormatCell}
                            field="testStartTime"
                            title="Start"
                            locked
                            width={80}
                            footerCell={commonFooterCell}
                        />
                        <GridColumn
                            sortable={true}
                            cell={TimeFormatCell}
                            field="testEnd"
                            title="End"
                            locked
                            width={80}
                            footerCell={commonFooterCell}
                        />
                        <GridColumn
                            sortable={true}
                            width={120}
                            field="duration"
                            title="Duration"
                            cell={DurationCell}
                            locked
                            footerCell={commonFooterCell}
                        />
                        <GridColumn
                            {...columnProps("companyName", dataState)}
                            cell={CustomerColumnCell}
                            field="companyName"
                            title="Customer Name"
                            width={180}
                            locked
                            footerCell={commonFooterCell}
                        />
                        <GridColumn
                            {...columnProps("registration", dataState)}
                            cell={VehicleRegColumnCell}
                            field="registration"
                            title="Vehicle Reg"
                            width={140}
                            locked
                            footerCell={commonFooterCell}
                        />
                        <GridColumn
                            {...columnProps("preparerID", dataState)}
                            cell={CustomColumnCell}
                            field="preparerID"
                            title="PreparerID"
                            width={150}
                        />
                        <GridColumn
                            {...columnProps("testStationName", dataState)}
                            cell={TestColumnCell}
                            field="testStationName"
                            title="Test Station"
                            width={150}
                        />
                        <GridColumn
                            {...columnProps("jobNumber", dataState)}
                            cell={CustomColumnCell}
                            field="jobNumber"
                            title="Job No."
                            width={140}
                        />
                        <GridColumn
                            {...columnProps("customerOrderRef", dataState)}
                            cell={CustomColumnCell}
                            field="customerOrderRef"
                            title="Customer Ref"
                            width={182}
                        />
                        <GridColumn
                            {...columnProps("bookingStatus", dataState)}
                            cell={CustomColumnCell}
                            field="bookingStatus"
                            title="Status"
                            width={101}
                        />
                        <GridColumn
                            {...columnProps("reminderCall", dataState)}
                            columnMenu={props => (
                                <GridColumnMenuCheckboxFilter {...props} data={bookings} expanded={true} />
                            )}
                            sortable={true}
                            cell={ReminderColumnCell}
                            field="reminderCall"
                            title="Reminder call"
                            width={150}
                        />
                        <GridColumn
                            {...columnProps("testFee", dataState)}
                            cell={FeeColumnCell}
                            field="testFee"
                            title="Test Fee"
                            width={120}
                            footerCell={totalFeeFooterCell}
                        />
                        <GridColumn
                            {...columnProps("laneFee", dataState)}
                            cell={FeeColumnCell}
                            field="laneFee"
                            title="Lane Fee"
                            width={120}
                            footerCell={totalFeeFooterCell}
                        />
                        <GridColumn
                            cell={(props: any) => {
                                const classCancelled = isBookingCancelledOrNoShow(props) ? "grid-cancelled-bk" : ""

                                return (
                                    <td className={`text-center pe-4  ${classCancelled}`}>
                                        {props.dataItem[`${props.field}`] !== 0
                                            ? intl.formatNumber(props.dataItem[`${props.field}`], {
                                                  style: "currency",
                                                  minimumFractionDigits: 2,
                                              })
                                            : ""}
                                    </td>
                                )
                            }}
                            field="total"
                            title="Total"
                            width={100}
                            footerCell={totalFeeFooterCell}
                        />
                    </Grid>
                </Tooltip>
            )}
        </div>
    )
}
export default MotGrid
